import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Check, X, Pencil, Eye, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';

type Enquiry = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  apartment: string;
  checkin: string;
  checkout: string;
  guests: number;
  message: string | null;
  total_nights: number | null;
  total_amount: number | null;
  discount_amount: number | null;
  coupon_code: string | null;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
};

export default function AdminEnquiries() {
  const { t } = useLanguage();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'declined'>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Enquiry>>({});
  const [viewingId, setViewingId] = useState<string | null>(null);

  const load = async () => {
    let q = supabase.from('enquiries').select('*').order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('status', filter);
    const { data } = await q;
    setEnquiries((data as Enquiry[]) ?? []);
  };

  useEffect(() => { load(); }, [filter]);

  const accept = async (enq: Enquiry) => {
    const { error: bookErr } = await supabase.from('bookings').insert({
      enquiry_id: enq.id,
      name: enq.name,
      email: enq.email,
      phone: enq.phone,
      apartment: enq.apartment,
      checkin: enq.checkin,
      checkout: enq.checkout,
      guests: enq.guests,
      total_amount: enq.total_amount ?? 0,
      discount_amount: enq.discount_amount,
    });
    if (bookErr) { toast.error(t('failedToCreate')); return; }

    await supabase.from('enquiries').delete().eq('id', enq.id);
    toast.success(t('bookingCreatedFor').replace('{name}', enq.name));
    load();
  };

  const decline = async (id: string) => {
    if (!confirm(t('declineDeleteConfirm'))) return;
    const { error } = await supabase.from('enquiries').delete().eq('id', id);
    if (error) { toast.error(t('failedToDelete') + ': ' + error.message); return; }
    toast.success(t('enquiryDeclinedDeleted'));
    load();
  };

  const deleteEnquiry = async (id: string, name: string) => {
    if (!confirm(t('deleteEnquiryConfirm').replace('{name}', name))) return;
    const { error } = await supabase.from('enquiries').delete().eq('id', id);
    if (error) { toast.error(t('failedToDelete') + ': ' + error.message); return; }
    toast.success(t('enquiryDeleted'));
    load();
  };

  const startEdit = (enq: Enquiry) => {
    setEditingId(enq.id);
    setEditForm({ name: enq.name, email: enq.email, phone: enq.phone, apartment: enq.apartment, checkin: enq.checkin, checkout: enq.checkout, guests: enq.guests, total_amount: enq.total_amount });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    await supabase.from('enquiries').update(editForm).eq('id', editingId);
    toast.success(t('enquiryUpdated'));
    setEditingId(null);
    load();
  };

  const filterLabels: Record<string, string> = {
    all: t('all'), pending: t('pending'), accepted: t('accepted'), declined: t('declined'),
  };

  const statusBadge = (s: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = { pending: t('pending'), accepted: t('accepted'), declined: t('declined') };
    return <span className={`px-2 py-1 rounded-sm text-xs font-medium ${styles[s] || ''}`}>{labels[s] || s}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-light">{t('enquiries')}</h1>
        <div className="flex gap-2">
          {(['all', 'pending', 'accepted', 'declined'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm transition-colors ${
                filter === f ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground border border-border hover:bg-muted'
              }`}>
              {filterLabels[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {enquiries.map(enq => (
          <div key={enq.id} className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <p className="font-medium">{enq.name}</p>
                <p className="text-xs text-muted-foreground">{enq.email}</p>
                {enq.phone && <p className="text-xs text-muted-foreground">{enq.phone}</p>}
              </div>
              <div className="text-sm capitalize font-serif">{enq.apartment}</div>
              <div className="text-xs">
                <p>{enq.checkin}</p>
                <p className="text-muted-foreground">→ {enq.checkout}</p>
              </div>
              <div className="text-sm">{enq.guests} {t('guestsLabel').toLowerCase()}</div>
              <div className="text-sm font-medium">{enq.total_amount ? `€${enq.total_amount}` : '—'}</div>
              <div>{statusBadge(enq.status)}</div>
              <div className="flex gap-1">
                <button onClick={() => setViewingId(viewingId === enq.id ? null : enq.id)} className="p-1.5 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Eye size={14} /></button>
                {enq.status === 'pending' && (
                  <>
                    <button onClick={() => accept(enq)} title={t('adminAccept')} className="p-1.5 rounded-sm bg-green-50 text-green-700 hover:bg-green-100"><Check size={14} /></button>
                    <button onClick={() => decline(enq.id)} title={t('adminDecline')} className="p-1.5 rounded-sm bg-red-50 text-red-700 hover:bg-red-100"><X size={14} /></button>
                    <button onClick={() => startEdit(enq)} title={t('edit')} className="p-1.5 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Pencil size={14} /></button>
                  </>
                )}
                <button onClick={() => deleteEnquiry(enq.id, enq.name)} title={t('delete')} className="p-1.5 rounded-sm bg-red-50 text-red-700 hover:bg-red-100"><Trash2 size={14} /></button>
              </div>
            </div>

            {(viewingId === enq.id || editingId === enq.id) && (
              <div className="border-t border-border p-4 bg-muted/10">
                {editingId === enq.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('name')}</label>
                      <input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('emailLabel')}</label>
                      <input value={editForm.email || ''} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('phoneLabel')}</label>
                      <input value={editForm.phone || ''} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('apartmentLabel')}</label>
                      <select value={editForm.apartment || ''} onChange={e => setEditForm(p => ({ ...p, apartment: e.target.value }))}
                        className="bg-transparent border border-border rounded-sm px-2 py-1 text-sm w-full">
                        <option value="hera">Hera</option>
                        <option value="zeus">Zeus</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('checkin')}</label>
                      <input type="date" value={editForm.checkin || ''} onChange={e => setEditForm(p => ({ ...p, checkin: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-xs focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('checkout')}</label>
                      <input type="date" value={editForm.checkout || ''} onChange={e => setEditForm(p => ({ ...p, checkout: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-xs focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('totalAmount')} (€)</label>
                      <input type="number" value={editForm.total_amount ?? ''} onChange={e => setEditForm(p => ({ ...p, total_amount: Number(e.target.value) }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none" />
                    </div>
                    <div className="md:col-span-2 flex gap-2 items-end">
                      <button onClick={saveEdit} className="flex items-center gap-1.5 bg-accent text-accent-foreground px-4 py-2 text-xs tracking-wider uppercase rounded-sm hover:bg-accent/90">
                        <Check size={14} /> {t('save')}
                      </button>
                      <button onClick={() => setEditingId(null)} className="flex items-center gap-1.5 px-4 py-2 text-xs tracking-wider uppercase text-muted-foreground border border-border rounded-sm hover:bg-muted">
                        <X size={14} /> {t('cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('guestsLabel')}</span>{enq.guests}</div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('nights')}</span>{enq.total_nights ?? '—'}</div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('discount')}</span>{enq.discount_amount ? `€${enq.discount_amount}` : '—'}</div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('coupon')}</span>{enq.coupon_code || '—'}</div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('submitted')}</span>{format(parseISO(enq.created_at), 'dd MMM yyyy HH:mm')}</div>
                    {enq.message && (
                      <div className="col-span-full">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('message')}</span>
                        <p className="mt-1 text-sm text-foreground/80 bg-muted/30 rounded-sm p-3">{enq.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {enquiries.length === 0 && (
          <div className="bg-card border border-border rounded-sm p-12 text-center text-muted-foreground">{t('noEnquiriesFound')}</div>
        )}
      </div>
    </div>
  );
}
